import { isEmpty } from "lodash";
import { useJsApiLoader } from "@react-google-maps/api";
import { types } from "@vilocnv/allsetra-core";
import {
  enrichMarkersWithObjectStatus,
  ObjectStatusValue,
  resolveObjectIconUrl,
  isInactiveObject
} from "./objectStatus.helpers";

export const useGoogleMapsLoader = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`,
    libraries: ["geometry"],
    language: "nl"
  });

  return { isLoaded, loadError };
};

function latRad(lat: any) {
  const sin = Math.sin((lat * Math.PI) / 180);
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
}

function zoom(mapPx: any, worldPx: any, fraction: any) {
  return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
}

export function getBoundsZoomLevel(
  coords: Array<{ latitude: number; longitude: number }>,
  mapDim: { height: number; width: number }
) {
  try {
    const bounds = new google.maps.LatLngBounds();

    coords.map((coord) =>
      bounds.extend({ lat: coord.latitude, lng: coord.longitude })
    );

    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  } catch (e: any) {
    console.log("getBoundsZoomLevel error", e.message);
  }
}

export type ExtractMarkersOptions = {
  statusLookup?: Record<string, ObjectStatusValue>;
  /** When false, DEACTIVATED markers are excluded. When undefined, no status filtering. */
  showInactiveObjects?: boolean;
};

export const extractObjectsDataAsMarkersForMap = (
  objects: Array<types.IObject> | Array<any>,
  options?: ExtractMarkersOptions
) => {
  if (isEmpty(objects)) return [];

  const markers = objects
    .filter(
      (item: any) =>
        item.location &&
        item.location.latitude !== null &&
        item.location.longitude !== null &&
        item.location.latitude !== 0 &&
        item.location.longitude !== 0
    )
    .map((item: any) => {
      const { latitude, longitude } = item?.location;
      const rawIconUrl = item?.objectType?.icon?.url || item?.icon?.url;
      const status =
        item?.status ??
        (item?.uniqueId && options?.statusLookup
          ? options.statusLookup[item.uniqueId]
          : undefined);
      const inactive = isInactiveObject(status);
      const iconUrl = resolveObjectIconUrl(rawIconUrl, status);

      const originalDate = new Date(item.location.date);
      const formattedDate = originalDate.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: false
      });

      return {
        //due to icons displacement issue
        lat: latitude - 0.00001,
        lng: longitude,
        iconUrl,
        name: item?.name,
        aNumber: item?.aNumber,
        accuracy: item?.location.accuracy,
        formattedDate,
        lastReportedSpeed: item?.lastReportedSpeed,
        lastReportedDirection: item?.lastReportedDirection,
        direction: item?.direction,
        uniqueId: item?.uniqueId,
        resolvedAddress: item?.location?.resolvedAddress || "N/A",
        status,
        isInactive: inactive
      };
    });

  if (options?.showInactiveObjects === undefined) {
    return markers;
  }

  return enrichMarkersWithObjectStatus(markers, options.statusLookup ?? {}, {
    showInactiveObjects: options.showInactiveObjects
  });
};

export const SINGLE_VEHICLE_ZOOM = 14;
export const MAP_OVERVIEW_ZOOM = 7;
export const MAP_FIT_BOUNDS_PADDING = 72;

export type MapLatLng = { lat: number; lng: number };

const readNumericCoord = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "function") {
    const result = value();
    if (typeof result === "number" && Number.isFinite(result)) return result;
  }
  return null;
};

export const isValidMapCoordinate = (
  lat?: number | null,
  lng?: number | null
): lat is number =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat !== 0 &&
  lng !== 0;

const toMapLatLng = (value: unknown): MapLatLng | null => {
  if (!value || typeof value !== "object") return null;

  const withPosition = value as { position?: unknown };
  if (withPosition.position && typeof withPosition.position === "object") {
    const position = withPosition.position as { lat?: unknown; lng?: unknown };
    const lat = readNumericCoord(position.lat);
    const lng = readNumericCoord(position.lng);
    if (lat !== null && lng !== null && isValidMapCoordinate(lat, lng)) {
      return { lat, lng };
    }
  }

  const item = value as { lat?: unknown; lng?: unknown };
  const lat = readNumericCoord(item.lat);
  const lng = readNumericCoord(item.lng);
  if (lat !== null && lng !== null && isValidMapCoordinate(lat, lng)) {
    return { lat, lng };
  }

  return null;
};

export const extractValidMapPositions = (items: unknown[]): MapLatLng[] =>
  items
    .map(toMapLatLng)
    .filter((position): position is MapLatLng => position !== null);

export const getVisibleUnitsSignature = (items: unknown[]): string =>
  items
    .filter((item) => toMapLatLng(item) !== null)
    .map((item) => (item as { uniqueId?: string })?.uniqueId ?? "")
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join("|");

export const fitMapToVisibleMarkers = (
  map: google.maps.Map | null | undefined,
  positions: MapLatLng[],
  options?: { padding?: number; singleVehicleZoom?: number }
): boolean => {
  if (!map || positions.length === 0) return false;

  const padding = options?.padding ?? MAP_FIT_BOUNDS_PADDING;
  const singleVehicleZoom = options?.singleVehicleZoom ?? SINGLE_VEHICLE_ZOOM;

  if (positions.length === 1) {
    map.panTo(positions[0]);
    map.setZoom(singleVehicleZoom);
    return true;
  }

  const bounds = new google.maps.LatLngBounds();
  positions.forEach((position) => bounds.extend(position));
  map.fitBounds(bounds, padding);
  return true;
};

export const focusMapOnSingleVehicle = (
  map: google.maps.Map | null | undefined,
  position: MapLatLng | null | undefined,
  zoom = SINGLE_VEHICLE_ZOOM
): boolean => {
  if (!map || !position || !isValidMapCoordinate(position.lat, position.lng)) {
    return false;
  }

  map.panTo(position);
  map.setZoom(zoom);
  return true;
};

export const resetMapToOverview = (
  map: google.maps.Map | null | undefined,
  center: MapLatLng,
  zoom = MAP_OVERVIEW_ZOOM
): boolean => {
  if (!map) return false;
  map.panTo(center);
  map.setZoom(zoom);
  return true;
};

export const getAddressFromLatLng = (lat: number, lng: number) => {
  return fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`
  )
    .then((response) => response.json())
    .then((data) => {
      if (data?.status === "OK") {
        const address = data?.results[0]?.formatted_address;
        return address;
      } else {
        throw new Error("Geocoding failed " + data.status);
      }
    })
    .catch((error) => {
      console.error("Error fetching address ", error.message);
    });
};
